import { AttachmentViewer } from "@app/components/assistant/conversation/attachment/AttachmentViewer";
import type { AttachmentCitation } from "@app/components/assistant/conversation/attachment/types";
import {
  isAudioContentType,
  isTextualContentType,
} from "@app/components/assistant/conversation/attachment/utils";
import { getFileFormat, isSupportedImageContentType } from "@app/types/files";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Citation,
  CitationClose,
  CitationDescription,
  CitationIcons,
  CitationImage,
  CitationTitle,
  cn,
  Icon,
  Tooltip,
} from "@dust-tt/sparkle";
import type React from "react";
import { useState } from "react";

interface AttachmentCitationProps {
  owner: LightWorkspaceType;
  attachmentCitation: AttachmentCitation;
  conversationId?: string | null;
}

export function AttachmentCitation({
  owner,
  attachmentCitation,
  conversationId,
}: AttachmentCitationProps) {
  const [viewerOpen, setViewerOpen] = useState(false);

  const tooltipContent =
    attachmentCitation.type === "file" ? (
      attachmentCitation.title
    ) : (
      <div className="flex flex-col gap-1">
        <div className="font-bold">{attachmentCitation.title}</div>
        <div className="flex gap-1 pt-1 text-sm">
          <Icon visual={attachmentCitation.spaceIcon} />
          <p>{attachmentCitation.spaceName}</p>
        </div>
        <div className="text-sm text-muted-foreground dark:text-muted-foreground-night">
          {attachmentCitation.path}
        </div>
      </div>
    );

  const isImage =
    attachmentCitation.type === "file" &&
    isSupportedImageContentType(attachmentCitation.contentType);

  const isLoading =
    attachmentCitation.type === "file" && attachmentCitation.isUploading;

  const canOpenInDialog =
    attachmentCitation.type === "file" &&
    getFileFormat(attachmentCitation.contentType)?.isSafeToDisplay &&
    (isTextualContentType(attachmentCitation) ||
      isAudioContentType(attachmentCitation));

  const dialogOrDownloadProps = canOpenInDialog
    ? {
        onClick: (e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault();
          setViewerOpen(true);
        },
      }
    : isImage
      ? {} // ImagePreview handles click with its own zoom dialog
      : {
          href: attachmentCitation.sourceUrl ?? undefined,
        };

  return (
    <>
      <Tooltip
        trigger={
          <Citation
            {...dialogOrDownloadProps}
            isLoading={isLoading}
            containerClassName={cn("s-h-full", isImage && "s-aspect-square")}
            action={
              !isImage &&
              attachmentCitation.onRemove && (
                <CitationClose
                  onClick={(e) => {
                    e.stopPropagation();
                    attachmentCitation.onRemove?.();
                  }}
                />
              )
            }
          >
            {isImage ? (
              <CitationImage
                imgSrc={`${attachmentCitation.sourceUrl}?action=view&version=processed`}
                title={attachmentCitation.title}
                downloadUrl={attachmentCitation.sourceUrl ?? undefined}
                onClose={attachmentCitation.onRemove}
              />
            ) : (
              <>
                <CitationIcons>{attachmentCitation.visual}</CitationIcons>
                <CitationTitle className="truncate text-ellipsis">
                  {attachmentCitation.title}
                </CitationTitle>
                {attachmentCitation.type === "file" &&
                  attachmentCitation.description && (
                    <CitationDescription className="truncate text-ellipsis">
                      {attachmentCitation.description}
                    </CitationDescription>
                  )}
                {attachmentCitation.type === "node" && (
                  <CitationDescription className="truncate text-ellipsis">
                    <span>
                      {attachmentCitation.path ?? attachmentCitation.spaceName}
                    </span>
                  </CitationDescription>
                )}
              </>
            )}
          </Citation>
        }
        label={tooltipContent}
      />
      {canOpenInDialog && (
        <AttachmentViewer
          setViewerOpen={setViewerOpen}
          viewerOpen={viewerOpen}
          attachmentCitation={attachmentCitation}
          owner={owner}
        />
      )}
    </>
  );
}
